import {
  Row,
  Col,
  Table,
  Avatar,
  DatePicker,
  DatePickerProps,
  theme,
  Space,
  Button,
  Form,
  Input,
  InputNumber,
  Select,
  Popconfirm,
  Typography,
  TimePicker,
  Tag,
  Switch,
  Tooltip,
  Card,
  Statistic,
  Divider,
} from "antd";
import {
  DeleteOutlined,
  ClockCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useContext, useEffect, useMemo, useState } from "react";
import { EmployeeOperation } from "../../../serviceoperation/EmployeeOperation";
import { CompanyContext } from "../../../contexts/company-context";
import dayjs from "dayjs";
import {
  BulkScheduleList,
  AddBulkScheduleFormProps,
  DayScheduleValue,
  DeletedDraftSchedulePreview,
  UpdatedDraftSchedulePreview,
} from "./schedule-bulkadd-entity";
import { EmployeeDetails } from "../../../serviceoperation/entity/EmployeeOperationEntity";
import { getInitials } from "../../../Util/stringutil";
import { ScheduleOperation } from "../../../serviceoperation/ScheduleOperation";
import { ScheduleList } from "../../../serviceoperation/entity/ScheduleOperationEntity";
import { ScheduleTempOperation } from "../../../serviceoperation/ScheduleTempOperation";
import { ScheduleTemplate } from "../../../types/scheduleTemplate";

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: string;
  inputType: "number" | "text" | "date" | "dropdown";
  record: BulkScheduleList;
  children: React.ReactNode;
}

const ScheduleBulkAdd: React.FC<AddBulkScheduleFormProps> = ({
  scheduleData,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [data, setData] = useState<BulkScheduleList[]>(scheduleData);
  const [disableAdd, setDisableAdd] = useState<boolean>(false);
  const [editingKey, setEditingKey] = useState<number>(-1);
  const { selectedCompany } = useContext(CompanyContext);
  const { useToken } = theme;
  const { token } = useToken();

  const currDate = new Date();

  // Sync data when scheduleData prop changes (e.g., on reset)
  useEffect(() => {
    setData(scheduleData);
    form.resetFields();
    setEditingKey(-1);
    setDisableAdd(false);
  }, [scheduleData, form]);

  // Selected Week Schedule Constants.
  const [selectedWeek, setSelectedWeek] = useState<string[]>([]);
  const daysOfWeek = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];

  // List of employee for a given logged in user.
  // This will contain a data regardless of who is logged in
  // as a logged in user will be in this list.
  const [employee, setEmployee] = useState<{ value: string; label: string }[]>(
    [],
  );
  const [employeeDetails, setEmployeeDetails] = useState<EmployeeDetails[]>([]);
  const [deletedDraftScheduleIds, setDeletedDraftScheduleIds] = useState<
    number[]
  >([]);
  const [deletedDraftSchedules, setDeletedDraftSchedules] = useState<
    DeletedDraftSchedulePreview[]
  >([]);
  const [updatedDraftSchedules, setUpdatedDraftSchedules] = useState<
    UpdatedDraftSchedulePreview[]
  >([]);

  const [usePreset, setUsePreset] = useState<boolean>(true);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null,
  );
  const [templates, setTemplates] = useState<ScheduleTemplate[]>([]);
  const tempOps = useMemo(() => new ScheduleTempOperation(), []);
  const [selectedPresetDays, setSelectedPresetDays] = useState<number[]>([
    0, 1, 2, 3, 4, 5, 6,
  ]);
  const presetDayOptions = useMemo(
    () => [
      { label: "Sun", value: 0 },
      { label: "Mon", value: 1 },
      { label: "Tue", value: 2 },
      { label: "Wed", value: 3 },
      { label: "Thu", value: 4 },
      { label: "Fri", value: 5 },
      { label: "Sat", value: 6 },
    ],
    [],
  );

  // Get the Current Week Dates based on any Date
  // This is to set the Table view Dynamically.
  // Make sure to add the offset of the
  const handleWeekChange = (selWeek: string) => {
    const current = new Date(selWeek);
    // So that this doesn't mess with the SetDate.
    current.setHours(0, 0, 0, 0);
    const week = [];
    for (let i = 0; i < 7; i++) {
      const first = current.getDate() - current.getDay() + i;
      const day = new Date(current.setDate(first)).toISOString().slice(0, 10);
      week.push(day);
    }
    setSelectedWeek(week);
    setDeletedDraftScheduleIds([]);
    setDeletedDraftSchedules([]);
    setUpdatedDraftSchedules([]);

    // This will remove the data that was fetched from the server
    // meaning data that can't be modified because the scedule already
    // exists.
    setData(data.filter((x) => !x.existingRow));

    // Process UseEffect call to get new Schedule... How to best do that?
    // Need to fetch existing schedule for the given week so that we can render the data...
  };

  useEffect(() => {
    handleWeekChange(currDate.toISOString());
  }, []);

  const employeeOps = useMemo(() => new EmployeeOperation(), []);

  useMemo(() => {
    const getEmployee = async () => {
      if (!selectedCompany) return;
      const employees = await employeeOps.getEmployeeAsync(
        selectedCompany.corporationId,
        1,
        255,
        true,
      );
      setEmployeeDetails(
        employees.results.filter(
          (employee) => employee.isActive, //&&
          //(employees.results.length <= 1 || employee.employeeId !== empId)
        ),
      );

      setEmployee(
        employees.results
          .filter(
            (employee) => employee.isActive, //&&
            // (employees.results.length <= 1 || employee.employeeId !== empId)
          )
          .map((employee) => ({
            value: employee.employeeId,
            label: `${employee.firstName} ${employee.lastName}`,
          })),
      );
    };
    getEmployee();
  }, [selectedCompany, employeeOps]);

  useEffect(() => {
    if (!selectedCompany) return;
    tempOps
      .getScheduleTempAsync(selectedCompany.corporationId, 1, 255)
      .then((value) => {
        const mapped = value.results.map((x) => ({
          id: x.templateId.toString(),
          name: x.name,
          description: x.name,
          startWorkHour: x.startHour,
          startWorkMin: x.startMinute,
          endWorkHour: x.endHour,
          endWorkMin: x.endMinute,
          isDefault: false,
          createdAt: x.createdDateTimeUTC,
          updatedAt: x.createdDateTimeUTC,
        }));
        setTemplates(mapped);

        if (value.rowCount === 0) setUsePreset(false);
      });
  }, [selectedCompany, tempOps]);

  const templateOptions = useMemo(() => {
    return templates.map((template) => {
      const start = dayjs()
        .hour(template.startWorkHour)
        .minute(template.startWorkMin)
        .format("hh:mm A");
      const end = dayjs()
        .hour(template.endWorkHour)
        .minute(template.endWorkMin)
        .format("hh:mm A");

      return {
        value: template.id,
        label: `${template.name} · ${start} - ${end}`,
      };
    });
  }, [templates]);

  const applyPresetToRow = (rowKey: React.Key) => {
    if (!selectedTemplateId) return;
    if (selectedPresetDays.length === 0) return;
    const template = templates.find((t) => t.id === selectedTemplateId);
    if (!template) return;
    const record = data.find((item) => item.key === rowKey);
    if (!record) return;
    if (!selectedWeek.length) return;

    const updates: Record<string, [dayjs.Dayjs, dayjs.Dayjs]> = {};

    daysOfWeek.forEach((day, index) => {
      if (!selectedPresetDays.includes(index)) return;
      const scheduleKey = `schedule${day}` as keyof BulkScheduleList;
      const current = record[scheduleKey] as
        | DayScheduleValue
        | [dayjs.Dayjs, dayjs.Dayjs]
        | undefined;
      const isLocked =
        Array.isArray(current) && current.length > 2 && !!current[2];
      const hasValue =
        Array.isArray(current) &&
        current.length >= 2 &&
        !!current[0] &&
        !!current[1];

      if (!isLocked && !hasValue) {
        const base = dayjs(selectedWeek[index]);
        updates[scheduleKey] = [
          base.hour(template.startWorkHour).minute(template.startWorkMin),
          base.hour(template.endWorkHour).minute(template.endWorkMin),
        ];
      }
    });

    form.setFieldsValue(updates);
  };

  const applyPresetToEditingRow = () => {
    if (editingKey === -1) return;
    applyPresetToRow(editingKey);
  };

  const applyPresetToAllRows = () => {
    if (!selectedTemplateId) return;
    if (selectedPresetDays.length === 0) return;
    const template = templates.find((t) => t.id === selectedTemplateId);
    if (!template) return;
    if (!selectedWeek.length) return;

    const newData = data.map((record) => {
      if (record.existingRow) return record; // Skip existing rows from server

      const updates: Record<
        string,
        [dayjs.Dayjs, dayjs.Dayjs] | DayScheduleValue | undefined
      > = {};
      daysOfWeek.forEach((day, index) => {
        if (!selectedPresetDays.includes(index)) return;
        const scheduleKey = `schedule${day}` as keyof BulkScheduleList;
        const current = record[scheduleKey] as
          | DayScheduleValue
          | [dayjs.Dayjs, dayjs.Dayjs]
          | undefined;
        const isLocked =
          Array.isArray(current) && current.length > 2 && !!current[2];
        const hasValue =
          Array.isArray(current) &&
          current.length >= 2 &&
          !!current[0] &&
          !!current[1];

        if (!isLocked && !hasValue) {
          const base = dayjs(selectedWeek[index]);
          updates[scheduleKey] = [
            base.hour(template.startWorkHour).minute(template.startWorkMin),
            base.hour(template.endWorkHour).minute(template.endWorkMin),
          ];
        }
      });

      return Object.keys(updates).length > 0
        ? { ...record, ...updates }
        : record;
    });

    setData(newData);
    onSuccess(
      newData,
      employee,
      employeeDetails,
      selectedWeek,
      deletedDraftScheduleIds,
      deletedDraftSchedules,
      updatedDraftSchedules,
    );
  };

  const collectDraftScheduleIdsFromRow = (
    record: BulkScheduleList,
  ): number[] => {
    const ids = new Set<number>();

    daysOfWeek.forEach((day) => {
      const scheduleKey = `schedule${day}` as keyof BulkScheduleList;
      const daySchedule = record[scheduleKey] as DayScheduleValue | undefined;
      if (!daySchedule) return;

      const scheduleId = daySchedule[3];

      if (scheduleId !== undefined) {
        ids.add(scheduleId);
      }
    });

    return Array.from(ids);
  };

  const handleDeleteRow = (key: React.Key) => {
    const recordToDelete = data.find((item) => item.key === key);
    const idsToDelete = recordToDelete
      ? collectDraftScheduleIdsFromRow(recordToDelete)
      : [];

    const previewToDelete: DeletedDraftSchedulePreview[] = [];
    if (recordToDelete) {
      const employeeName =
        employee.find((x) => x.value === recordToDelete.name)?.label ||
        recordToDelete.name;

      daysOfWeek.forEach((day, index) => {
        const scheduleKey = `schedule${day}` as keyof BulkScheduleList;
        const daySchedule = recordToDelete[scheduleKey] as
          | DayScheduleValue
          | undefined;

        if (!daySchedule || daySchedule[3] === undefined) {
          return;
        }

        previewToDelete.push({
          scheduleId: daySchedule[3],
          employeeId: recordToDelete.name,
          employeeName,
          workDay:
            selectedWeek[index] || dayjs(daySchedule[0]).format("YYYY-MM-DD"),
          startTime: dayjs(daySchedule[0]).format("hh:mm A"),
          endTime: dayjs(daySchedule[1]).format("hh:mm A"),
        });
      });
    }

    const updatedData = data.filter((item) => item.key !== key);
    setData(updatedData);

    const nextDeletedIds = Array.from(
      new Set([...deletedDraftScheduleIds, ...idsToDelete]),
    );
    setDeletedDraftScheduleIds(nextDeletedIds);

    const nextDeletedSchedules = Array.from(
      new Map(
        [...deletedDraftSchedules, ...previewToDelete].map((item) => [
          item.scheduleId,
          item,
        ]),
      ).values(),
    );
    setDeletedDraftSchedules(nextDeletedSchedules);

    const nextUpdatedSchedules = updatedDraftSchedules.filter(
      (item) => !idsToDelete.includes(item.scheduleId),
    );
    setUpdatedDraftSchedules(nextUpdatedSchedules);

    if (editingKey === key) {
      setEditingKey(-1);
      setDisableAdd(false);
    }
    onSuccess(
      updatedData,
      employee,
      employeeDetails,
      selectedWeek,
      nextDeletedIds,
      nextDeletedSchedules,
      nextUpdatedSchedules,
    );
  };

  const calculateTotalHours = (record: BulkScheduleList): number => {
    let total = 0;
    daysOfWeek.forEach((day) => {
      const scheduleKey = `schedule${day}` as keyof BulkScheduleList;
      const daySchedule = record[scheduleKey] as DayScheduleValue | undefined;
      if (daySchedule && daySchedule[0] && daySchedule[1]) {
        const hours = dayjs(daySchedule[1]).diff(
          dayjs(daySchedule[0]),
          "hour",
          true,
        );
        total += hours;
      }
    });
    return parseFloat(total.toFixed(2));
  };

  const getRowStatus = (
    record: BulkScheduleList,
  ): "complete" | "partial" | "empty" => {
    const scheduledDays = daysOfWeek.filter((day) => {
      const scheduleKey = `schedule${day}` as keyof BulkScheduleList;
      const daySchedule = record[scheduleKey] as DayScheduleValue | undefined;
      return (
        daySchedule &&
        Array.isArray(daySchedule) &&
        daySchedule[0] &&
        daySchedule[1]
      );
    }).length;

    if (scheduledDays === selectedPresetDays.length) return "complete";
    if (scheduledDays > 0) return "partial";
    return "empty";
  };

  const EditableCell: React.FC<EditableCellProps> = ({
    editing,
    dataIndex,
    title,
    inputType,
    record,
    children,
    ...restProps
  }) => {
    const isDisabled = () => {
      // Access the specific schedule array based on dataIndex
      // and check if the edit should be disabled or not.
      if (dataIndex.startsWith("schedule")) {
        const scheduleKey = dataIndex as keyof BulkScheduleList;
        const scheduleValue = record[scheduleKey] as
          | DayScheduleValue
          | undefined;
        if (scheduleValue && scheduleValue[2]) {
          return true;
        }
      }
      return false;
    };

    const inputNode =
      inputType === "number" ? (
        <InputNumber />
      ) : inputType === "date" ? (
        <TimePicker.RangePicker
          minuteStep={5}
          format="hh:mm A"
          disabled={isDisabled()}
        />
      ) : inputType === "dropdown" ? (
        <Select
          disabled={!record.newRow}
          placeholder="Select Employee"
          key={record.key}
          value={record.name}
          options={employee.filter(
            (emp) => !data.find((da) => emp.value === da.name),
          )}
        />
      ) : (
        <Input />
      );
    return (
      <td {...restProps}>
        {editing && inputType !== "date" ? (
          <Form.Item
            name={dataIndex}
            style={{ margin: 0 }}
            rules={[
              {
                required: true,
                message: `Please Input ${title}!`,
              },
            ]}
          >
            {inputNode}
          </Form.Item>
        ) : editing && inputType === "date" ? (
          <Form.Item
            name={dataIndex}
            style={{ margin: 0 }}
            //rules={[
            //  {
            //    required: true,
            //    validator: validateDatetime(record.key),
            //  },
            //]}
          >
            {inputNode}
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  };

  const edit = (record: Partial<BulkScheduleList> & { key: React.Key }) => {
    const rowForForm = { ...record } as Record<string, unknown>;

    daysOfWeek.forEach((day) => {
      const scheduleKey = `schedule${day}`;
      const dayValue = rowForForm[scheduleKey] as DayScheduleValue | undefined;
      if (Array.isArray(dayValue) && dayValue.length >= 2) {
        rowForForm[scheduleKey] = [dayValue[0], dayValue[1]];
      }
    });

    form.setFieldsValue(rowForForm);
    setEditingKey(record.key);
    setDisableAdd(true);

    if (usePreset && selectedTemplateId) {
      applyPresetToRow(record.key);
    }
  };

  const save = async (key: React.Key) => {
    try {
      const row = (await form.validateFields()) as BulkScheduleList;

      const newData = [...data];
      const index = newData.findIndex((item) => key === item.key);
      const item = newData[index];

      let nextDeletedIds = [...deletedDraftScheduleIds];
      let nextDeletedSchedules = [...deletedDraftSchedules];
      let nextUpdatedSchedules = [...updatedDraftSchedules];

      if (index > -1) {
        item.newRow = false;
        const mergedItem = {
          ...item,
          ...row,
        };

        const employeeName =
          employee.find((x) => x.value === item.name)?.label || item.name;
        const deletedIdsSet = new Set<number>(nextDeletedIds);
        const deletedMap = new Map<number, DeletedDraftSchedulePreview>(
          nextDeletedSchedules.map((entry) => [entry.scheduleId, entry]),
        );
        const updatedMap = new Map<number, UpdatedDraftSchedulePreview>(
          nextUpdatedSchedules.map((entry) => [entry.scheduleId, entry]),
        );

        daysOfWeek.forEach((day, dayIndex) => {
          const scheduleKey = `schedule${day}` as keyof BulkScheduleList;
          const previousValue = item[scheduleKey] as
            | DayScheduleValue
            | undefined;
          const nextValue = row[scheduleKey] as
            | [dayjs.Dayjs, dayjs.Dayjs]
            | DayScheduleValue
            | undefined;

          const previousScheduleId = previousValue?.[3];
          const workDay =
            selectedWeek[dayIndex] ||
            dayjs(previousValue?.[0] || new Date()).format("YYYY-MM-DD");

          if (Array.isArray(nextValue) && nextValue.length >= 2) {
            const wasExistingDraft =
              previousValue !== undefined && previousValue[3] !== undefined;
            const changedFromExisting =
              wasExistingDraft &&
              (!dayjs(previousValue[0]).isSame(nextValue[0]) ||
                !dayjs(previousValue[1]).isSame(nextValue[1]));

            (
              mergedItem as unknown as Record<
                string,
                DayScheduleValue | undefined
              >
            )[scheduleKey] = [
              nextValue[0],
              nextValue[1],
              previousValue?.[2] ?? false,
              previousValue?.[3],
              changedFromExisting,
              previousValue?.[5] ?? previousValue?.[0],
              previousValue?.[6] ?? previousValue?.[1],
            ] as DayScheduleValue;

            if (previousScheduleId !== undefined) {
              deletedIdsSet.delete(previousScheduleId);
              deletedMap.delete(previousScheduleId);

              if (changedFromExisting) {
                updatedMap.set(previousScheduleId, {
                  scheduleId: previousScheduleId,
                  employeeId: item.name,
                  employeeName,
                  workDay,
                  originalStartTime: dayjs(
                    previousValue?.[5] ?? previousValue?.[0],
                  ).format("hh:mm A"),
                  originalEndTime: dayjs(
                    previousValue?.[6] ?? previousValue?.[1],
                  ).format("hh:mm A"),
                  updatedStartTime: dayjs(nextValue[0]).format("hh:mm A"),
                  updatedEndTime: dayjs(nextValue[1]).format("hh:mm A"),
                });
              } else {
                updatedMap.delete(previousScheduleId);
              }
            }
          } else if (previousScheduleId !== undefined) {
            (
              mergedItem as unknown as Record<
                string,
                DayScheduleValue | undefined
              >
            )[scheduleKey] = undefined;

            deletedIdsSet.add(previousScheduleId);
            deletedMap.set(previousScheduleId, {
              scheduleId: previousScheduleId,
              employeeId: item.name,
              employeeName,
              workDay,
              startTime: dayjs(previousValue?.[0]).format("hh:mm A"),
              endTime: dayjs(previousValue?.[1]).format("hh:mm A"),
            });
            updatedMap.delete(previousScheduleId);
          }
        });

        nextDeletedIds = Array.from(deletedIdsSet);
        nextDeletedSchedules = Array.from(deletedMap.values());
        nextUpdatedSchedules = Array.from(updatedMap.values());

        setDeletedDraftScheduleIds(nextDeletedIds);
        setDeletedDraftSchedules(nextDeletedSchedules);
        setUpdatedDraftSchedules(nextUpdatedSchedules);

        newData.splice(index, 1, mergedItem);
        setData(newData);
        setEditingKey(-1);
      } else {
        newData.push(row);
        setData(newData);
        setEditingKey(-1);
      }
      // This is a call back to Parent so that it can have
      // all the data in the world.
      onSuccess(
        newData,
        employee,
        employeeDetails,
        selectedWeek,
        nextDeletedIds,
        nextDeletedSchedules,
        nextUpdatedSchedules,
      );

      setDisableAdd(false);
    } catch (errInfo) {
      console.log("Validate Failed:", errInfo);
    }
  };

  function convertResultsToBulkScheduleList(
    schedule: ScheduleList,
  ): BulkScheduleList[] {
    const employeeMap = new Map<string, BulkScheduleList>();

    schedule.results.forEach((result) => {
      const {
        employee,
        workDate,
        startWorkHour,
        startWorkMinute,
        endWorkHour,
        endWorkMinute,
        isDraft,
        scheduleID,
      } = result;
      const employeeId = employee.employeeID;

      // Create dayjs objects for the start and end times
      const start = dayjs(workDate)
        .set("hour", startWorkHour)
        .set("minute", startWorkMinute);
      const end = dayjs(workDate)
        .set("hour", endWorkHour)
        .set("minute", endWorkMinute);

      // If the employee is not already in the map, add them with default values
      if (!employeeMap.has(employeeId)) {
        employeeMap.set(employeeId, {
          key: Date.now() + (Math.floor(Math.random() * (100 - 1 + 1)) + 1),
          name: employeeId,
          schedulesunday: undefined,
          schedulemonday: undefined,
          scheduletuesday: undefined,
          schedulewednesday: undefined,
          schedulethursday: undefined,
          schedulefriday: undefined,
          schedulesaturday: undefined,
          newRow: false,
          existingRow: true,
        });
      }

      const schedule = employeeMap.get(employeeId);
      if (!schedule) return;
      // Get the day of the week (0 = Sunday, 6 = Saturday)
      const dayOfWeek = start.day();
      const isLocked = !(isDraft ?? false);

      // Update the schedule for the correct day of the week
      switch (dayOfWeek) {
        case 0:
          schedule.schedulesunday = [
            start,
            end,
            isLocked,
            scheduleID,
            false,
            start,
            end,
          ];
          break;
        case 1:
          schedule.schedulemonday = [
            start,
            end,
            isLocked,
            scheduleID,
            false,
            start,
            end,
          ];
          break;
        case 2:
          schedule.scheduletuesday = [
            start,
            end,
            isLocked,
            scheduleID,
            false,
            start,
            end,
          ];
          break;
        case 3:
          schedule.schedulewednesday = [
            start,
            end,
            isLocked,
            scheduleID,
            false,
            start,
            end,
          ];
          break;
        case 4:
          schedule.schedulethursday = [
            start,
            end,
            isLocked,
            scheduleID,
            false,
            start,
            end,
          ];
          break;
        case 5:
          schedule.schedulefriday = [
            start,
            end,
            isLocked,
            scheduleID,
            false,
            start,
            end,
          ];
          break;
        case 6:
          schedule.schedulesaturday = [
            start,
            end,
            isLocked,
            scheduleID,
            false,
            start,
            end,
          ];
          break;
      }
    });
    return Array.from(employeeMap.values());
  }

  useEffect(() => {
    const scheduleOps = new ScheduleOperation();
    scheduleOps
      .getScheduleAsync(selectedCompany?.corporationId as number, {
        weekOf: selectedWeek[0],
        page: 1,
        pageSize: 255,
      })
      .then((x) => {
        const rows = convertResultsToBulkScheduleList(x);
        const newData = [...data];
        rows.forEach((row) => {
          // This is the index where the Employee Exsists on a given table.
          // So on this case let's just look at the current data.
          const index = newData.findIndex((item) =>
            newData.find(() => row.name === item.name),
          );

          const item = newData[index];
          if (index > -1) {
            //const item = newData[index];
            // Set Row to not new data...
            item.newRow = false;
            newData.splice(index, 1, {
              ...item,
              ...row,
            });
            //setEditingKey(-1);
          } else {
            newData.push(row);
            //setEditingKey(-1);
          }
        });
        setData(newData);
      });
  }, [selectedWeek]);

  const cancel = (value?: BulkScheduleList) => {
    setEditingKey(-1);
    if (value?.newRow) {
      const updatedData = [...data].filter((item) => item.key !== value.key);
      setData(updatedData);
    }
    setDisableAdd(false);
  };

  const handleAdd = () => {
    const newData = {
      key: Date.now(),
      name: "",
      schedulesunday: undefined,
      schedulemonday: undefined,
      scheduletuesday: undefined,
      schedulewednesday: undefined,
      schedulethursday: undefined,
      schedulefriday: undefined,
      schedulesaturday: undefined,
      newRow: true,
      existingRow: false,
    };
    const updatedData = [...data, newData];
    setData(updatedData);
    edit(newData);
  };

  const totalEmployeeHours = data.reduce(
    (sum, record) => sum + calculateTotalHours(record),
    0,
  );

  const Dayscolumns = daysOfWeek.map((day, index) => ({
    title: `${new Date(selectedWeek[index]).toLocaleDateString("en-us", {
      timeZone: "UTC",
      weekday: "short",
      month: "short",
      day: "numeric",
    })}`,
    dataIndex: `schedule${day}`,
    //width: "12%",
    editable: true,
    render: (_: never, rec: BulkScheduleList) => {
      const dataIndex = `schedule${day}`;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const daySchedule = (rec as any)[dataIndex]; // Using bracket notation to access the dynamic property
      if (daySchedule) {
        const startTime = dayjs(daySchedule[0]);
        const endTime = dayjs(daySchedule[1]);
        return (
          <Tag color="orange">
            {`${dayjs(startTime)?.format("hh:mm A")} - ${dayjs(endTime)?.format(
              "hh:mm A",
            )}`}
          </Tag>
        );
      }
      return null;
    },
  }));

  const columns = [
    {
      title: "Employee",
      dataIndex: "name",
      width: "12%",
      editable: true,
      render(nameValue: string) {
        return (
          <div>
            <Avatar
              style={{ backgroundColor: "#f56a00", verticalAlign: "middle" }}
              size="default"
              gap={4}
            >
              {getInitials(
                employee
                  .filter((x) => x.value === nameValue)
                  .map((map) => map.label)
                  .at(0) ?? "",
              )}
            </Avatar>
            &nbsp;
            {employee
              .filter((x) => x.value === nameValue)
              .map((map) => map.label)
              .at(0)}
          </div>
        );
      },
    },
    ...Dayscolumns,
    {
      title: "Total Hours",
      dataIndex: "hours",
      width: "8%",
      render: (_: never, record: BulkScheduleList) => {
        const hours = calculateTotalHours(record);
        return (
          <Tooltip title="Total scheduled hours">
            <Space>
              <ClockCircleOutlined style={{ fontSize: 14 }} />
              <span style={{ fontWeight: 500 }}>{hours.toFixed(2)}h</span>
            </Space>
          </Tooltip>
        );
      },
    },
    {
      title: "Action",
      dataIndex: "operation",
      width: "10%",
      editable: false,
      render: (_: never, record: BulkScheduleList) => {
        const editable = isEditing(record);
        return editable ? (
          <span>
            <Typography.Link
              onClick={() => save(record.key)}
              style={{ marginRight: 8 }}
            >
              Save
            </Typography.Link>
            <Popconfirm title="Are you sure?" onConfirm={() => cancel(record)}>
              <a>Cancel</a>
            </Popconfirm>
          </span>
        ) : (
          <Space size="small">
            <Typography.Link
              disabled={editingKey !== -1}
              onClick={() => edit(record)}
            >
              Edit
            </Typography.Link>
            <Popconfirm
              title="Delete row?"
              onConfirm={() => handleDeleteRow(record.key)}
            >
              <DeleteOutlined
                style={{
                  color: "red",
                  cursor: "pointer",
                  fontSize: 14,
                }}
              />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  const isEditing = (record: BulkScheduleList) => record.key === editingKey;

  const onWeekChange: DatePickerProps["onChange"] = (date) => {
    // Have to set the Time of the day as well, otherwise it offsets the day by 1.
    //handleWeekChange(currDate.toDateString());
    handleWeekChange(date?.toISOString() as string);
  };

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: BulkScheduleList) => ({
        record,
        inputType: col.dataIndex.includes("day")
          ? "date"
          : col.dataIndex === "name"
          ? "dropdown"
          : "text",
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });

  return (
    <div>
      <div style={{ backgroundColor: token.colorBgContainer }}>
        <Row gutter={16}>
          <Col xs={24} sm={24} md={24} lg={12} style={{ padding: 20 }}>
            <Space size={6}>
              <DatePicker
                defaultValue={dayjs(currDate)}
                onChange={onWeekChange}
                picker="week"
                disabledDate={(current) => {
                  const customDate = dayjs().startOf("day");
                  return current && current.isBefore(customDate);
                }}
              />
            </Space>
          </Col>
          <Col
            xs={24}
            sm={24}
            md={24}
            lg={12}
            style={{ padding: 20, display: "flex", justifyContent: "flex-end" }}
          >
            <Space size={8} wrap>
              <Switch
                checkedChildren="Preset"
                unCheckedChildren="Manual"
                checked={usePreset}
                onChange={(checked) => {
                  setUsePreset(checked);
                  if (!checked) setSelectedTemplateId(null);
                }}
                disabled={templates.length === 0}
              />
              <Select
                placeholder="Preset"
                allowClear
                style={{ minWidth: 220 }}
                options={templateOptions}
                value={selectedTemplateId}
                onChange={(value) => setSelectedTemplateId(value)}
                disabled={!usePreset || templates.length === 0}
              />
              <Select
                mode="multiple"
                maxTagCount="responsive"
                placeholder="Days"
                style={{ minWidth: 180 }}
                options={presetDayOptions}
                value={selectedPresetDays}
                onChange={(value) => setSelectedPresetDays(value)}
                disabled={!usePreset}
              />
              <Button
                type="default"
                onClick={applyPresetToEditingRow}
                disabled={
                  !usePreset ||
                  !selectedTemplateId ||
                  editingKey === -1 ||
                  selectedPresetDays.length === 0
                }
              >
                Apply to Row
              </Button>
              <Button
                onClick={applyPresetToAllRows}
                disabled={
                  !usePreset ||
                  !selectedTemplateId ||
                  data.length === 0 ||
                  selectedPresetDays.length === 0
                }
              >
                Apply to All
              </Button>
              {usePreset && selectedTemplateId ? (
                <Typography.Text type="secondary">Preset ready</Typography.Text>
              ) : null}
            </Space>
          </Col>
        </Row>
        <Divider style={{ margin: "12px 0" }} />
        <Row gutter={16} style={{ padding: "0 20px 20px 20px" }}>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="Total Employees"
                value={data.length}
                valueStyle={{ color: token.colorPrimary }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="Total Hours"
                value={totalEmployeeHours.toFixed(2)}
                suffix="h"
                valueStyle={{ color: token.colorSuccess }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="Complete"
                value={
                  data.filter((r) => getRowStatus(r) === "complete").length
                }
                valueStyle={{ color: "#52c41a" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="Needs Attention"
                value={
                  data.filter((r) => getRowStatus(r) !== "complete").length
                }
                valueStyle={{
                  color:
                    data.filter((r) => getRowStatus(r) !== "complete").length >
                    0
                      ? "#faad14"
                      : token.colorTextTertiary,
                }}
              />
            </Card>
          </Col>
        </Row>
      </div>
      <Row gutter={16} style={{ marginTop: "16px" }}>
        <Col span={24}>
          <Form form={form} component={false}>
            <Table
              components={{
                body: {
                  cell: EditableCell,
                },
              }}
              bordered
              style={{ whiteSpace: "pre" }}
              dataSource={data}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              columns={mergedColumns as any}
              rowClassName="editable-row"
              pagination={false}
            />
            <Button
              type="primary"
              onClick={handleAdd}
              disabled={disableAdd}
              style={{ marginTop: 16, marginBottom: 16 }}
            >
              <PlusOutlined /> Add Employee
            </Button>
          </Form>
        </Col>
      </Row>
    </div>
  );
};

export default ScheduleBulkAdd;
